import React, { useState, useEffect } from 'react';
import { Mail, Bell, BellOff, Send, Loader2, Check, UserCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import axios from 'axios';
import { useAuth, API } from '../../App';
import { toast } from 'sonner';
import { formatErrorDetail } from '../../utils/formatError';

const Settings = () => {
  const { token, user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [coaches, setCoaches] = useState([]);
  const [selectedCoach, setSelectedCoach] = useState(user?.coach || 'brian');
  const [savingCoach, setSavingCoach] = useState(false);
  const [prefs, setPrefs] = useState({
    enabled: false,
    frequency: 'weekly',
    email: ''
  });

  useEffect(() => {
    fetchPrefs();
    fetchCoaches();
  }, []);

  const fetchCoaches = async () => {
    try {
      const res = await axios.get(`${API}/coaches`);
      setCoaches(res.data.coaches);
    } catch (e) { /* ignore */ }
  };

  const saveCoach = async () => {
    setSavingCoach(true);
    try {
      const res = await axios.post(
        `${API}/auth/select-coach`,
        { coach_id: selectedCoach },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      if (refreshUser) await refreshUser();
    } catch (e) {
      toast.error('Could not change coach');
    }
    setSavingCoach(false);
  };

  const fetchPrefs = async () => {
    try {
      const res = await axios.get(`${API}/digest/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrefs(res.data.preferences);
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
    setLoading(false);
  };

  const savePrefs = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/digest/preferences`, prefs, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Preferences saved');
    } catch (error) {
      toast.error('Could not save preferences');
    }
    setSaving(false);
  };

  const sendTestDigest = async () => {
    setSendingTest(true);
    try {
      const res = await axios.post(`${API}/digest/send-test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
    } catch (error) {
      const detail = formatErrorDetail(error.response?.data?.detail) || 'Could not send test email';
      toast.error(detail);
    }
    setSendingTest(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-black/40" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl headline text-black mb-2" data-testid="settings-heading">Settings</h1>
        <p className="text-black/60 body-text">Manage your notifications and preferences.</p>
      </div>

      {/* Coach Selection */}
      <div className="bg-white rounded-none border border-border overflow-hidden" data-testid="coach-settings">
        <div className="px-6 py-4 border-b border-border flex items-center gap-3">
          <div className="w-10 h-10 rounded-none bg-[#F6ADBC]/10 flex items-center justify-center">
            <UserCircle2 className="h-5 w-5 text-[#F6ADBC]" />
          </div>
          <div>
            <h2 className="subhead text-black">Your Coach</h2>
            <p className="text-sm text-black/50">Change your coaching avatar anytime</p>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-4">
            {coaches.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelectedCoach(c.id)}
                className={`relative rounded-none p-2 text-center transition-all border-2 ${
                  selectedCoach === c.id
                    ? 'border-[#F6ADBC] bg-[#F6ADBC]/5 '
                    : 'border-transparent hover:bg-[#F9F9F9]'
                }`}
                data-testid={`settings-coach-${c.id}`}
              >
                {selectedCoach === c.id && (
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-[#F6ADBC] rounded-none flex items-center justify-center">
                    <Check className="h-3 w-3 text-[#1E491A]" />
                  </div>
                )}
                <div className="w-12 h-12 mx-auto rounded-none overflow-hidden bg-[#F5F5F5] mb-1">
                  <img src={c.avatar} alt={c.name} className="w-full h-full object-cover" />
                </div>
                <p className="text-xs font-medium text-black">{c.name}</p>
              </button>
            ))}
          </div>
          {selectedCoach !== user?.coach && (
            <Button
              onClick={saveCoach}
              disabled={savingCoach}
              className="bg-[#F6ADBC] text-[#1E491A] hover:bg-[#e6c200] rounded-none font-medium"
              data-testid="save-coach-btn"
            >
              {savingCoach && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Switch to {coaches.find(c => c.id === selectedCoach)?.name}
            </Button>
          )}
        </div>
      </div>

      {/* Email Digest Section */}
      <div className="bg-white rounded-none border border-border overflow-hidden" data-testid="digest-settings">
        <div className="px-6 py-4 border-b border-border flex items-center gap-3">
          <div className="w-10 h-10 rounded-none bg-[#F6ADBC]/10 flex items-center justify-center">
            <Mail className="h-5 w-5 text-[#F6ADBC]" />
          </div>
          <div>
            <h2 className="subhead text-black">Email Digest</h2>
            <p className="text-sm text-black/50">Get a summary of your tasks and NZ business news</p>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {prefs.enabled ? (
                <Bell className="h-5 w-5 text-[#1E491A]" />
              ) : (
                <BellOff className="h-5 w-5 text-black/30" />
              )}
              <div>
                <p className="font-medium text-black">
                  {prefs.enabled ? 'Digest enabled' : 'Digest disabled'}
                </p>
                <p className="text-sm text-black/50">
                  {prefs.enabled ? 'You\'ll receive regular email summaries' : 'Enable to get email updates'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setPrefs({ ...prefs, enabled: !prefs.enabled })}
              className={`relative w-12 h-7 rounded-none transition-colors ${
                prefs.enabled ? 'bg-[#F6ADBC]' : 'bg-[#E5E5E5]'
              }`}
              data-testid="digest-toggle"
            >
              <div className={`absolute top-1 w-5 h-5 rounded-none bg-white shadow transition-transform ${
                prefs.enabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          {prefs.enabled && (
            <>
              {/* Frequency */}
              <div>
                <label className="text-sm font-medium text-black mb-2 block">Frequency</label>
                <div className="flex gap-2">
                  {[
                    { value: 'weekly', label: 'Weekly' },
                    { value: 'daily', label: 'Daily' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setPrefs({ ...prefs, frequency: opt.value })}
                      className={`px-4 py-2 rounded-none text-sm transition-all ${
                        prefs.frequency === opt.value
                          ? 'bg-[#F6ADBC] text-black font-medium'
                          : 'bg-[#F0F0F0] text-black/60 hover:bg-[#E5E5E5]'
                      }`}
                      data-testid={`freq-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="text-sm font-medium text-black mb-2 block">Email address</label>
                <Input
                  type="email"
                  value={prefs.email}
                  onChange={(e) => setPrefs({ ...prefs, email: e.target.value })}
                  placeholder="your@email.com"
                  className="bg-[#F9F9F9] border-border rounded-none"
                  data-testid="digest-email"
                />
              </div>
            </>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <Button
              onClick={savePrefs}
              disabled={saving}
              className="bg-[#F6ADBC] text-[#1E491A] hover:bg-[#e6c200] rounded-none font-medium"
              data-testid="save-prefs-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Preferences
            </Button>
            {prefs.enabled && (
              <Button
                variant="outline"
                onClick={sendTestDigest}
                disabled={sendingTest}
                className="rounded-none"
                data-testid="send-test-btn"
              >
                {sendingTest ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Send Test Email
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="bg-[#F9F9F9] rounded-none p-5">
        <p className="text-sm text-black/60">
          Your digest includes: pending tasks, upcoming deadlines, programme progress, and the latest NZ business news curated for entrepreneurs.
        </p>
      </div>
    </div>
  );
};

export default Settings;
